import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Float, Text3D, Center, Sphere, useTexture } from '@react-three/drei';
import { motion, AnimatePresence } from 'framer-motion';
import * as THREE from 'three';
import { Button } from './ui/button';
import { SkipForward } from 'lucide-react';

// Bottle component with animation
const Bottle = ({ config, onBubblesRelease }) => {
  const meshRef = useRef();
  const [phase, setPhase] = useState('falling'); // falling, filling, sinking
  const [fillLevel, setFillLevel] = useState(0);
  const startTime = useRef(Date.now());

  useFrame(() => {
    if (!meshRef.current) return;

    const elapsed = Date.now() - startTime.current;
    const { fallDuration, fillDuration, sinkDuration, sinkDepth, rotationSpeed } = config.bottle;

    // Falling phase
    if (phase === 'falling' && elapsed < fallDuration) {
      const progress = elapsed / fallDuration;
      const y = config.bottle.initialPosition[1] * (1 - progress);
      meshRef.current.position.y = y;
      meshRef.current.rotation.x += rotationSpeed;
      meshRef.current.rotation.z += rotationSpeed * 0.5;

      // Hit water surface
      if (progress > 0.95 && phase === 'falling') {
        setPhase('filling');
        startTime.current = Date.now();
      }
    }

    // Filling phase
    if (phase === 'filling' && elapsed < fillDuration) {
      const progress = elapsed / fillDuration;
      setFillLevel(progress);
      meshRef.current.position.y = 0 + Math.sin(progress * Math.PI * 4) * 0.2; // Bob in water

      if (progress > 0.95) {
        setPhase('sinking');
        startTime.current = Date.now();
        onBubblesRelease();
      }
    }

    // Sinking phase
    if (phase === 'sinking' && elapsed < sinkDuration) {
      const progress = elapsed / sinkDuration;
      const y = -progress * Math.abs(sinkDepth);
      meshRef.current.position.y = y;
      meshRef.current.rotation.x += rotationSpeed * 0.5;
    }
  });

  return (
    <group ref={meshRef} position={config.bottle.initialPosition}>
      {/* Bottle body */}
      <mesh castShadow>
        <cylinderGeometry args={[0.3, 0.4, 2, 16]} />
        <meshStandardMaterial
          color={config.bottle.color}
          transparent
          opacity={0.7}
          roughness={0.3}
          metalness={0.5}
          emissive="#8B4513"
          emissiveIntensity={0.2}
        />
      </mesh>
      
      {/* Bottle neck */}
      <mesh position={[0, 1.2, 0]} castShadow>
        <cylinderGeometry args={[0.15, 0.25, 0.4, 16]} />
        <meshStandardMaterial
          color={config.bottle.color}
          transparent
          opacity={0.7}
          roughness={0.3}
          metalness={0.5}
          emissive="#8B4513"
          emissiveIntensity={0.2}
        />
      </mesh>

      {/* Water inside bottle */}
      {fillLevel > 0 && (
        <mesh position={[0, -1 + fillLevel * 2, 0]}>
          <cylinderGeometry args={[0.28, 0.38, fillLevel * 2, 16]} />
          <meshStandardMaterial
            color="#4080ff"
            transparent
            opacity={0.6}
            roughness={0.2}
          />
        </mesh>
      )}
    </group>
  );
};

// Individual bubble component
const Bubble = ({ position, color, label, delay, finalPosition, config }) => {
  const meshRef = useRef();
  const [isReleased, setIsReleased] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const startPos = useRef(position);
  const targetPos = useRef(finalPosition);

  useEffect(() => {
    setTimeout(() => {
      setIsReleased(true);
    }, delay);
  }, [delay]);

  useFrame((state) => {
    if (!meshRef.current) return;

    if (isReleased) {
      const time = state.clock.getElapsedTime();
      
      // Smooth transition to final position
      meshRef.current.position.x = THREE.MathUtils.lerp(
        meshRef.current.position.x,
        targetPos.current[0],
        0.02
      );
      meshRef.current.position.y = THREE.MathUtils.lerp(
        meshRef.current.position.y,
        targetPos.current[1],
        0.02
      );
      meshRef.current.position.z = THREE.MathUtils.lerp(
        meshRef.current.position.z,
        targetPos.current[2],
        0.02
      );

      // Gentle floating motion
      meshRef.current.position.y += Math.sin(time * 2 + delay) * 0.01;
      meshRef.current.position.x += Math.cos(time * 1.5 + delay) * 0.005;

      // Check if close to final position
      const dist = meshRef.current.position.distanceTo(new THREE.Vector3(...targetPos.current));
      if (dist < 0.5 && !showLabel) {
        setShowLabel(true);
      }
    } else {
      // Rising from bottle
      meshRef.current.position.y += config.bubbles.floatSpeed * 0.01;
    }
  });

  return (
    <group ref={meshRef} position={startPos.current}>
      <Sphere args={[1.2, 32, 32]}>
        <meshStandardMaterial
          color={color}
          emissive={color}
          emissiveIntensity={0.5}
          metalness={0.8}
          roughness={0.2}
          transparent
          opacity={0.95}
        />
      </Sphere>
      
      {showLabel && (
        <Float speed={1.5} rotationIntensity={0.2} floatIntensity={0.3}>
          <Center position={[0, 1.8, 0]}>
            <Text3D
              font="/fonts/helvetiker_regular.typeface.json"
              size={0.25}
              height={0.08}
              curveSegments={12}
            >
              {label}
              <meshStandardMaterial color="white" emissive="white" emissiveIntensity={0.5} />
            </Text3D>
          </Center>
        </Float>
      )}
    </group>
  );
};

// Underwater particles
const UnderwaterParticles = ({ config }) => {
  const particlesRef = useRef();
  const particleCount = config.underwater.particleCount;

  const positions = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);

  for (let i = 0; i < particleCount; i++) {
    positions[i * 3] = (Math.random() - 0.5) * 30;
    positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
    velocities[i * 3] = (Math.random() - 0.5) * 0.02;
    velocities[i * 3 + 1] = Math.random() * 0.02;
    velocities[i * 3 + 2] = (Math.random() - 0.5) * 0.02;
  }

  useFrame(() => {
    if (!particlesRef.current) return;

    const positions = particlesRef.current.geometry.attributes.position.array;

    for (let i = 0; i < particleCount; i++) {
      positions[i * 3] += velocities[i * 3];
      positions[i * 3 + 1] += velocities[i * 3 + 1];
      positions[i * 3 + 2] += velocities[i * 3 + 2];

      // Wrap particles
      if (positions[i * 3 + 1] > 10) positions[i * 3 + 1] = -10;
      if (positions[i * 3] > 15) positions[i * 3] = -15;
      if (positions[i * 3] < -15) positions[i * 3] = 15;
    }

    particlesRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <points ref={particlesRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={particleCount}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={config.underwater.particleSize}
        color="#88ccff"
        transparent
        opacity={0.6}
        sizeAttenuation
      />
    </points>
  );
};

// Main 3D Scene
const IntroScene = ({ config, onComplete }) => {
  const [bubblesReleased, setBubblesReleased] = useState(false);
  const [bubbles, setBubbles] = useState([]);
  const { camera } = useThree();

  useEffect(() => {
    // Smooth camera animation to final position
    const duration = config.camera.transitionDuration;
    const startPos = camera.position.clone();
    const targetPos = new THREE.Vector3(...config.camera.finalPosition);
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic

      camera.position.lerpVectors(startPos, targetPos, eased);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    animate();
  }, [camera, config]);

  const handleBubblesRelease = () => {
    setBubblesReleased(true);
    
    // Release bubbles with staggered delays
    const newBubbles = config.bubbles.colors.map((color, index) => ({
      id: index,
      color,
      label: config.bubbles.labels[index],
      delay: config.bubbles.startDelay + index * config.bubbles.releaseInterval,
      finalPosition: config.bubbles.finalPositions[index],
      position: [0, -1, 0] // Start from bottle
    }));
    
    setBubbles(newBubbles);

    // Complete intro after all animations
    setTimeout(() => {
      onComplete();
    }, config.timing.totalDuration);
  };

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={config.underwater.lightIntensity * 0.3} />
      <pointLight position={[0, 10, 5]} intensity={config.underwater.lightIntensity} color="#88ccff" />
      <pointLight position={[-10, 5, -5]} intensity={config.underwater.lightIntensity * 0.5} color="#4080ff" />
      
      {/* Fog for underwater depth */}
      <fog
        attach="fog"
        args={[
          config.underwater.fogColor,
          config.underwater.fogNear,
          config.underwater.fogFar
        ]}
      />

      {/* Water surface effect (plane above) */}
      <mesh position={[0, 1, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[50, 50]} />
        <meshStandardMaterial
          color="#0066cc"
          transparent
          opacity={0.3}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Bottle */}
      <Bottle config={config} onBubblesRelease={handleBubblesRelease} />

      {/* Bubbles */}
      {bubbles.map((bubble) => (
        <Bubble
          key={bubble.id}
          position={bubble.position}
          color={bubble.color}
          label={bubble.label}
          delay={bubble.delay}
          finalPosition={bubble.finalPosition}
          config={config}
        />
      ))}

      {/* Underwater particles */}
      <UnderwaterParticles config={config} />
    </>
  );
};

// Main component
const CinematicIntro = ({ onComplete }) => {
  const [config, setConfig] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [canSkip, setCanSkip] = useState(false);
  const audioContextRef = useRef(null);
  const voiceSynthRef = useRef(null);

  useEffect(() => {
    // Load configuration
    fetch('/config/intro-config.json')
      .then((res) => res.json())
      .then((data) => {
        setConfig(data);
        if (data.intro.enabled) {
          playVoiceOver(data);
        }
      })
      .catch((err) => console.error('Failed to load intro config:', err));

    // Enable skip after delay
    setTimeout(() => {
      setCanSkip(true);
    }, 3000);
  }, []);

  const playVoiceOver = (cfg) => {
    if (!cfg.intro.autoPlayAudio) return;

    // Use Web Speech API for voice
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(cfg.intro.voiceText);
      utterance.lang = cfg.intro.voiceLang;
      utterance.rate = cfg.intro.voiceRate;
      utterance.pitch = cfg.intro.voicePitch;
      utterance.volume = cfg.audio.voiceVolume;

      setTimeout(() => {
        window.speechSynthesis.speak(utterance);
      }, 500);

      voiceSynthRef.current = utterance;
    }

    // Play splash sound at the right moment
    setTimeout(() => {
      playSplashSound(cfg);
    }, cfg.audio.splashDelay);
  };

  const playSplashSound = (cfg) => {
    // Create splash sound with Web Audio API
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }

    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.setValueAtTime(200, ctx.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.5);

    gainNode.gain.setValueAtTime(cfg.audio.splashVolume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.5);
  };

  const handleSkip = () => {
    if (voiceSynthRef.current) {
      window.speechSynthesis.cancel();
    }
    setIsPlaying(false);
    onComplete();
  };

  const handleComplete = () => {
    setTimeout(() => {
      setIsPlaying(false);
      onComplete();
    }, config?.timing?.transitionToMainDelay || 1000);
  };

  if (!config || !isPlaying) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* 3D Canvas */}
      <Canvas
        camera={{
          position: config.camera.initialPosition,
          fov: config.camera.fov
        }}
        style={{
          background: `linear-gradient(to bottom, ${config.underwater.backgroundColor}, ${config.underwater.gradientColor})`
        }}
      >
        <Suspense fallback={null}>
          <IntroScene config={config} onComplete={handleComplete} />
        </Suspense>
      </Canvas>

      {/* Skip button */}
      <AnimatePresence>
        {canSkip && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="absolute top-8 right-8 z-50"
          >
            <Button
              onClick={handleSkip}
              variant="outline"
              className="bg-white/10 text-white border-white/30 hover:bg-white/20 backdrop-blur-sm"
            >
              <SkipForward className="w-4 h-4 mr-2" />
              Passer l'intro
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Subtitle for voice */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 1 }}
        className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-40"
      >
        <p className="text-white text-2xl font-light text-center italic px-8 drop-shadow-lg">
          "{config.intro.voiceText}"
        </p>
      </motion.div>
    </div>
  );
};

export default CinematicIntro;
